'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Link2, Copy, Check, RefreshCw, Search, ExternalLink } from 'lucide-react';
import AppLayout from '@/components/AppLayout';
import StatusBadge from '@/components/ui/StatusBadge';
import { TableRowSkeleton } from '@/components/ui/LoadingSkeleton';
import { surveyService } from '@/lib/services/surveyService';
import type { SurveyRecord } from '@/types/survey';

const SURVEY_DOMAIN =
  process.env.NEXT_PUBLIC_SURVEY_DOMAIN ||
  process.env.NEXT_PUBLIC_SITE_URL ||
  'https://surveytrac3635.builtwithrocket.new';

function buildRedirectUrl(record: SurveyRecord): string {
  return `${SURVEY_DOMAIN}/survey/${record.status}/pid=${record.pid}/uid=${record.username}`;
}

export default function ShareableLinksPage() {
  const [records, setRecords] = useState<SurveyRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadRecords = useCallback(async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);
    try {
      const data = await surveyService.getAll();
      setRecords(data);
    } catch (err) {
      console.log('Failed to load records:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleCopy = async (record: SurveyRecord) => {
    const url = buildRedirectUrl(record);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(record.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // fallback
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedId(record.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const filtered = records.filter((r) => {
    const q = search.toLowerCase();
    return (
      r.pid.toLowerCase().includes(q) ||
      r.username.toLowerCase().includes(q) ||
      r.status.toLowerCase().includes(q)
    );
  });

  return (
    <AppLayout>
      <div className="min-h-screen bg-background p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
              <Link2 size={20} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Shareable Redirect Links</h1>
              <p className="text-sm text-muted-foreground">
                Copy and share redirect URLs with clients. Domain:{' '}
                <span className="text-primary font-medium">{SURVEY_DOMAIN}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by PID, username, or status…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-all"
            />
          </div>
          <button
            onClick={() => loadRecords(true)}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/10 transition-all disabled:opacity-50"
          >
            <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Domain info banner */}
        <div className="mb-5 p-3 rounded-lg bg-primary/5 border border-primary/15 flex items-start gap-2.5">
          <ExternalLink size={14} className="text-primary mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Redirect URL format:{' '}
            <code className="text-primary bg-primary/10 px-1.5 py-0.5 rounded text-xs">
              {SURVEY_DOMAIN}/survey/[status]/pid=XXXX/uid=XXXX
            </code>
            . To change the domain, update the{' '}
            <code className="text-primary bg-primary/10 px-1.5 py-0.5 rounded text-xs">
              NEXT_PUBLIC_SURVEY_DOMAIN
            </code>{' '}
            environment variable and redeploy.
          </p>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableRowSkeleton key={`skel-${i}`} cols={5} />
                ))}
              </tbody>
            </table>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Link2 size={36} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">{search ? 'No records match your search.' : 'No survey records found.'}</p>
          </div>
        ) : (
          <div className="rounded-xl border border-border overflow-hidden">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-white/3">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">PID</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Username (UID)</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Redirect URL</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Copy</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((record, idx) => {
                    const url = buildRedirectUrl(record);
                    const isCopied = copiedId === record.id;
                    return (
                      <tr
                        key={record.id}
                        className={`border-b border-border/50 transition-colors hover:bg-white/3 ${idx % 2 === 0 ? '' : 'bg-white/1'}`}
                      >
                        <td className="px-4 py-3 font-mono text-xs text-foreground">{record.pid}</td>
                        <td className="px-4 py-3 text-foreground">{record.username}</td>
                        <td className="px-4 py-3">
                          <StatusBadge status={record.status} />
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <span className="font-mono text-xs text-primary/80 truncate block" title={url}>
                            {url}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleCopy(record)}
                            title="Copy redirect URL"
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                              isCopied
                                ? 'bg-green-500/15 text-green-400 border border-green-500/25' :'bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20'
                            }`}
                          >
                            {isCopied ? <Check size={12} /> : <Copy size={12} />}
                            {isCopied ? 'Copied!' : 'Copy'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-border/50">
              {filtered.map((record) => {
                const url = buildRedirectUrl(record);
                const isCopied = copiedId === record.id;
                return (
                  <div key={record.id} className="p-4 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">PID:</span>
                        <span className="font-mono text-xs text-foreground font-medium">{record.pid}</span>
                      </div>
                      <StatusBadge status={record.status} />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">UID:</span>
                      <span className="text-xs text-foreground">{record.username}</span>
                    </div>
                    <div className="p-2 rounded-lg bg-white/3 border border-border/50">
                      <p className="font-mono text-xs text-primary/80 break-all">{url}</p>
                    </div>
                    <button
                      onClick={() => handleCopy(record)}
                      className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-all ${
                        isCopied
                          ? 'bg-green-500/15 text-green-400 border border-green-500/25' :'bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20'
                      }`}
                    >
                      {isCopied ? <Check size={13} /> : <Copy size={13} />}
                      {isCopied ? 'Copied!' : 'Copy Redirect URL'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer count */}
        {!isLoading && filtered.length > 0 && (
          <p className="mt-4 text-xs text-muted-foreground text-right">
            Showing {filtered.length} of {records.length} records
          </p>
        )}
      </div>
    </AppLayout>
  );
}
